import React, { useMemo } from 'react';
import { type useScrollspy } from '@sg/garnish';
import {
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  useCustomizationContext,
  useCustomizationHelpers,
} from '@sg/shared-fe/features/customization';

import { CustomizationIngredientsGrid } from '@order/features/customization';

import { CustomizationModificationsGroupCard } from '../../../../components';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationModificationGroup = (props: Props) => {
  const {
    actor,
    modificationGroup,
    activeStepperId,
    formattedQuantity,
    helperText,
    scrollSpy,
    resetStepperTimeout,
    setActiveStepperId,
    onAddIngredient,
    onRemoveIngredient,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { addIngredient, removeIngredient, sortModifications } =
    useCustomizationHelpers({ actor, onAddIngredient, onRemoveIngredient });

  const {
    modificationLimits,
    maxModificationsCount,
    activeModificationsMap,
    initialModificationsMap,
  } = useCustomizationContext({ actor });

  // ─── Derived Data ─────────────────────────────────────────────────────

  const modifications = useMemo(
    () => sortModifications(initialModificationsMap, modificationGroup),
    [sortModifications, initialModificationsMap, modificationGroup],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomizationIngredientsGrid.Section
      key={modificationGroup.id}
      sectionId={modificationGroup.id}
      ingredientKindName={modificationGroup.displayName}
      register={scrollSpy.helpers.target.register}
      deregister={scrollSpy.helpers.target.deregister}
      helperText={helperText}
      formattedQuantity={formattedQuantity}
    >
      <CustomizationIngredientsGrid.SectionStack>
        {modifications.map((modification) => (
          <CustomizationModificationsGroupCard
            key={modification.id}
            activeModificationsMap={activeModificationsMap}
            activeStepperId={activeStepperId}
            groupLimits={modificationLimits?.[modificationGroup.name]}
            maxModificationsCount={maxModificationsCount}
            modification={modification}
            onIncrement={addIngredient}
            onDecrement={removeIngredient}
            resetStepperTimeout={resetStepperTimeout}
            setActiveStepperId={setActiveStepperId}
          />
        ))}
      </CustomizationIngredientsGrid.SectionStack>
    </CustomizationIngredientsGrid.Section>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: CustomizationMachineActorRef;
  modificationGroup: CustomizationMachineTypes.ProductModifierGroup;
  activeStepperId: string | null;
  helperText?: string;
  formattedQuantity?: string;
  scrollSpy: ReturnType<typeof useScrollspy>;
  onAddIngredient?: (ingredientId: string) => void;
  onRemoveIngredient?: (ingredientId: string) => void;
  resetStepperTimeout: () => void;
  setActiveStepperId: (id: string | null) => void;
};
